// Your circles' rows and their logic (33-accounts.md, the accounts view — since v1.58.35 Your circles,
// 07-frame.md Design A; 36-desyr-id.md, the switcher): one row per circle, its row menu, then Add a
// circle (or the open invitations) and Manage my desyr ID; "Sign out of all accounts?" and the "In
// your other account" screen (07). A switch shows the chosen circle's Home skeleton at once (33).
import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import moreHoriz from '@material-symbols/svg-400/outlined/more_horiz.svg?raw';
import { Avatar } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { Confirm } from '../blocks/Dialog';
import { Icon, icons } from '../blocks/icons';
import { Menu, MenuItem, MenuLine } from '../blocks/Menu';
import { config, idOnly } from '../core/config';
import { circlesNow, loadCircles, syncCircles, useCircles } from '../id/circles';
import { signInAgain } from '../id/oidc';
import { leave } from '../id/Leave';
import { keepAfter, keepReturn, readId } from '../id/store';
import { roundDone, roundTrip, signInFor } from '../id/pressed';
import { frameIcons } from '../frame/icons';
import { JoinPage } from '../frame/JoinPage';
import { t } from '../i18n';
import { signOut } from '../session/client';
import { signOutBy } from '../core/device';
import { addressOf } from '../settings/Account';
import { circleLoading, circleName, circlePicture, countOf, onAccounts, othersCount, otherAccountFor, readCircles, removeWaiting, type Count } from './accounts';
import { serverOf } from '../core/session';
import { badgeCount, idColour } from '../core/format';
import { defaultAccount, setDefault, startAdding, switchTo, waiting, type Account } from './store';

/** The sessions call says this circle needs a passkey sign-in first (36, Behaviour 6.3). */
const needsPasskey = (server: string) => circlesNow().given?.[server] === 'passkey';

/**
 * A signed-out account's press: with desyr ID the sessions call again, the circle now a new one
 * (36, Behaviour 6.3), and it opens once ready; in the `passkey` state desyr ID's passkey sign-in starts
 * (Sign in again); else (or when it isn't) its sign-in page adds it again.
 */
async function signInAgainTo(a: Account): Promise<void> {
  const id = config().desyrId && readId() ? config().desyrId : undefined;
  if (id && needsPasskey(serverOf(a.user_id))) return signInAgain(id);
  await removeWaiting(a);
  const outcome = id && (await syncCircles(config()));
  if (outcome && 'answer' in outcome) roundDone();
  if (outcome && 'answer' in outcome && waiting().some((w) => w.user_id === a.user_id && !w.signed_out)) return switchTo(a.user_id);
  // desyr ID's own session is over (Behaviour 1.4 and 6.4): its sign-in once, and the switch goes on by itself after it.
  if (id && outcome && 'error' in outcome && outcome.error === 'sign-in' && roundTrip())
    return signInFor(new Error('desyr.id.sign_in'), () => keepAfter({ do: 'switch', mxid: a.user_id })).catch(() => startAdding());
  if (id && needsPasskey(serverOf(a.user_id))) return signInAgain(id);
  startAdding();
}

/** Draws again when a waiting account's name, picture or sign-in changes. */
function useAccounts() {
  const [, redraw] = useState(0);
  useEffect(() => onAccounts(() => redraw((n) => n + 1)), []);
  return waiting();
}

/** A circle's logo (33): its picture, else its initials on its ID colour, in a rounded square. */
export const CircleLogo = ({ userId, size }: { userId: string; size: number }) => <Avatar id={serverOf(userId)} name={circleName(userId)} src={circlePicture(userId)} size={size} round={false} letters={2} />;

/** The other accounts' news together, kept current (the rail's dot). */
export const useOthersCount = () => (useAccounts(), othersCount());

/** A waiting account's count (33): "99+" above 99, red when one is for the person; in words for screen readers. */
export const AccountBadge = ({ count }: { count?: Count }) =>
  count ? (
    <>
      <span className={count.red ? 'unread red' : 'unread'} aria-hidden="true" data-desyr-account-count={count.n}>
        {badgeCount(count.n)}
      </span>
      <span className="visually-hidden">{t('accounts.newMessages', { n: count.n })}</span>
    </>
  ) : null;

/** A circle of Your circles: on this device (the open one or a waiting account), or one needing a passkey sign-in first (36, 6.3). */
export type CircleRow = { mxid: string; server: string; name: string; account?: Account; here: boolean; picture?: string };

/** The person's circles A to Z (the order of ⌘1–⌘9) and the pick of one: the open one does nothing, another switches. */
export function useCircleRows(client: MatrixClient) {
  const others = useAccounts();
  const me = client.getUserId()!;
  const id = config().desyrId && readId() ? config().desyrId : undefined;
  const { answer, given, logos } = useCircles();
  const row = (mxid: string, account?: Account): CircleRow => ({ mxid, server: serverOf(mxid), name: circleName(mxid), account, here: true });
  const rows: CircleRow[] = [
    row(me),
    ...others.map((a) => row(a.user_id, a)),
    // A circle that needs a passkey sign-in first reads "Signed out — sign in again" (36, Behaviour 6.3).
    ...(id ? (answer?.circles ?? []).filter((c) => given?.[c.server] === 'passkey' && !others.some((a) => a.user_id === c.mxid)) : []).map((c) => ({ mxid: c.mxid, server: c.server, name: c.name, here: false, picture: logos[c.slug] })),
  ].sort((x, y) => x.name.localeCompare(y.name));
  const pick = (r: CircleRow) => {
    if (r.mxid === me) return;
    if (!r.here) return void signInAgain(id!);
    if (r.account?.signed_out) return void signInAgainTo(r.account);
    switchTo(r.mxid, '/', r.name);
  };
  return { rows, pick, me, id, answer };
}
export type Circles = ReturnType<typeof useCircleRows>;

/**
 * Your circles' content (07, Design A): the rows — tile, name (Default on the default), address or
 * "Signed out — sign in again", the check on the open one, another's count — each with its row menu
 * (More options or a right-click: Set as default, Leave); a line, then Add a circle (with desyr ID
 * its note; while invitations are open, Invitations in its place) and Manage my desyr ID.
 */
export function CircleItems({ client, circles, onPick, onClose, onDialog }: { client: MatrixClient; circles: Circles; onPick: (r: CircleRow) => void; onClose: () => void; onDialog: (d: 'invitations') => void }) {
  const { rows, me, id, answer } = circles;
  const [rowMenu, setRowMenu] = useState<{ row: CircleRow; point?: { x: number; y: number }; anchor?: HTMLElement }>();
  const [note, setNote] = useState(false);
  const leaving = rowMenu && answer?.circles.find((c) => c.mxid === rowMenu.row.mxid);
  const invitations = answer?.invitations?.length ?? 0;
  // The circles' profiles and the open invitations (37), asked again as it opens.
  useEffect(() => readCircles(client), [client]);
  useEffect(() => void (id && loadCircles(id)), []);
  const foot = (icon: string, label: string, onClick: () => void, className = '', end?: ReactNode) => (
    <button type="button" className={`circle-pick circle-foot ${className}`} onClick={onClick}>
      <span className="circle-foot-tile"><Icon svg={icon} size={20} /></span>
      <span className="row-name">{label}</span>
      {end}
    </button>
  );
  if (note) return <div className="circles-note" role="note"><p>{t('circles.note', { email: readId()?.email ?? '' })}</p><Button autoFocus onClick={onClose}>{t('common.close')}</Button></div>;
  return (
    <>
      <div className="circle-rows">
        {rows.map((r) => {
          const open = r.mxid === me;
          const loading = r.here && circleLoading(r.mxid);
          const out = !r.here || r.account?.signed_out;
          return (
            // One box per row, its "⋯" drawn inside the row's button at its end (07, One shape per row, v1.58.64; circles.css).
            <div key={r.mxid} className="circle-row" style={{ '--c': `var(--name-${idColour(r.server)})` } as CSSProperties}>
              <button type="button" className="circle-pick" aria-current={open || undefined} data-desyr-account={r.here ? r.mxid : undefined} data-desyr-account-signed-out={out ? '' : undefined}
                data-desyr-circle={id ? r.server : undefined} data-desyr-circle-state={id ? (r.here ? (out ? (needsPasskey(r.server) ? 'passkey' : 'failed') : 'ready') : 'passkey') : undefined}
                onClick={() => onPick(r)} onContextMenu={(e) => (e.preventDefault(), setRowMenu({ row: r, point: { x: e.clientX, y: e.clientY } }))}>
                {loading ? <i className="bone" data-desyr-skeleton="" aria-hidden="true" /> : r.here ? <CircleLogo userId={r.mxid} size={40} /> : <Avatar id={r.server} name={r.name} src={r.picture} size={40} round={false} letters={2} />}
                <span className="status-who">
                  <b>{loading ? <i className="bone" aria-hidden="true" /> : r.name}{r.here && defaultAccount() === r.mxid && <span className="circle-default">{t('circles.default')}</span>}</b>
                  <span>{out ? t('accounts.signedOut') : addressOf(r.mxid)}</span>
                </span>
                {open ? <Icon svg={icons.check} size={18} /> : <AccountBadge count={r.account && countOf(r.account)} />}
              </button>
              <button type="button" className="circle-more" aria-label={t('circles.more', { circle: r.name })} onClick={(e) => setRowMenu({ row: r, anchor: e.currentTarget })}><Icon svg={moreHoriz} size={18} /></button>
            </div>
          );
        })}
      </div>
      {rowMenu && (
        <Menu label={rowMenu.row.name} point={rowMenu.point} anchor={rowMenu.anchor} onClose={() => setRowMenu(undefined)}>
          <MenuItem icon={frameIcons.star} disabled={!rowMenu.row.here || defaultAccount() === rowMenu.row.mxid} onSelect={() => setDefault(rowMenu.row.mxid)}>{t('id.setDefault')}</MenuItem>
          {leaving && <MenuItem icon={frameIcons.logout} onSelect={() => (onClose(), leave(leaving.slug))}>{t('id.leaveItem', { circle: leaving.name })}</MenuItem>}
        </Menu>
      )}
      <MenuLine />
      {id && invitations > 0
        ? foot(frameIcons.mail, t('id.invitations'), () => (onClose(), onDialog('invitations')), '', <span className="unread">{badgeCount(invitations)}</span>)
        : foot(frameIcons.add, t('circles.add'), () => (idOnly() ? setNote(true) : (onClose(), startAdding())), 'add')}
      {id && (
        <a className="circle-pick circle-foot" href={id.account} onClick={() => keepReturn(location.pathname + location.search)}>
          <span className="circle-foot-tile"><Icon svg={icons.person} size={20} /></span>
          <span className="row-name">{t('id.manage')}</span>
        </a>
      )}
    </>
  );
}

/** "Sign out of all accounts" in Settings and You (33; not in Your circles, v1.58.35): without desyr ID, with other accounts here. */
export function SignOutAllEntry({ client, you }: { client: MatrixClient; you?: boolean }) {
  const [asking, setAsking] = useState(false);
  useAccounts();
  if (config().desyrId || !waiting().length) return null;
  const icon = <Icon svg={frameIcons.logout} size={20} />;
  return (
    <>
      <button type="button" className={you ? 'row you-row critical' : 'settings-entry critical'} onClick={() => setAsking(true)}>
        {you ? <span className="settings-icon">{icon}</span> : icon}
        <span className="row-name">{t('accounts.signOutAll')}</span>
      </button>
      {asking && <SignOutAll client={client} onClose={() => setAsking(false)} />}
    </>
  );
}

/** Sign out of all (33): every waiting account goes, the default is forgotten, then the open one signs out. */
export function SignOutAll({ client, onClose }: { client: MatrixClient; onClose: () => void }) {
  const others = waiting();
  return (
    <Confirm
      narrow
      destructive
      title={t('accounts.allTitle')}
      text={t('accounts.allLine', { n: others.length, total: others.length + 1 })}
      action={t('accounts.allButton')}
      busyText={t('accounts.signingOut')}
      onClose={onClose}
      onConfirm={async () => {
        const by = signOutBy(); // 03, Deadlines (v1.58.74)
        for (const a of others) await removeWaiting(a, by);
        setDefault(null);
        await signOut(client, undefined, by);
      }}
    />
  );
}

/** A room this account doesn't know: a waiting account that is in it offers to switch (nothing while asked). */
export function JoinOrOther({ client, refName }: { client: MatrixClient; refName: string }) {
  const [other, setOther] = useState<Account | null | undefined>(() => (refName.startsWith('!') && waiting().length ? undefined : null));
  useEffect(() => {
    if (other === undefined) void otherAccountFor(refName).then((a) => setOther(a ?? null));
  }, []);
  if (other === undefined) return null;
  if (!other) return <JoinPage client={client} refName={refName} />;
  return (
    <div className="hero" data-desyr-other-account="">
      <Icon svg={frameIcons.chat} size={48} />
      <h2>{t('accounts.otherTitle')}</h2>
      <p>{t('accounts.otherLine', { address: addressOf(other.user_id) })}</p>
      <Button onClick={() => switchTo(other.user_id, location.pathname + location.search + location.hash, circleName(other.user_id))}>{t('accounts.switchOpen')}</Button>
    </div>
  );
}
